import type { BerThongSheet } from '../../services/berThongService'

export function sheetBetLabel(sheet: Pick<BerThongSheet, 'betType'>) {
  return sheet.betType === 'top2' ? '2 ตัวบน' : '2 ตัวล่าง'
}

export function formatCloseTime(value?: string | null) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatCountdown(closeAt?: string | null, nowMs = Date.now()) {
  if (!closeAt) return ''
  const end = new Date(closeAt).getTime()
  if (Number.isNaN(end)) return ''
  const diff = end - nowMs
  if (diff <= 0) return 'ปิดรับแล้ว'
  const totalSec = Math.floor(diff / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 48) {
    const days = Math.floor(h / 24)
    return `เหลือ ${days} วัน`
  }
  if (h > 0) return `เหลือ ${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `เหลือ ${m}:${String(s).padStart(2, '0')}`
}

export function isClosingSoon(closeAt?: string | null, nowMs = Date.now()) {
  if (!closeAt) return false
  const end = new Date(closeAt).getTime()
  if (Number.isNaN(end)) return false
  const mins = (end - nowMs) / 60000
  return mins > 0 && mins <= 30
}

export function isBettingClosed(
  sheet: Pick<BerThongSheet, 'status' | 'closeAt'> | null | undefined,
  nowMs = Date.now(),
) {
  if (!sheet) return true
  if (sheet.status !== 'open') return true
  if (!sheet.closeAt) return false
  const end = new Date(sheet.closeAt).getTime()
  if (Number.isNaN(end)) return false
  return end <= nowMs
}

export function isSheetBuyable(sheet: BerThongSheet, nowMs = Date.now()) {
  return sheet.status === 'open' && !isBettingClosed(sheet, nowMs)
}

export type BerThongHuayGroup = {
  key: string
  huayId: string
  huayName: string
  openSheets: BerThongSheet[]
  closedSheets: BerThongSheet[]
  soonestCloseAt: string | null
}

export function huayKeyOf(sheet: Pick<BerThongSheet, 'huayId' | 'huayName'>) {
  const id = String(sheet.huayId || '').trim()
  if (id) return `id:${id}`
  const name = String(sheet.huayName || 'อื่นๆ').trim() || 'อื่นๆ'
  return `name:${encodeURIComponent(name)}`
}

export function parseHuayKey(huayKey: string): { huayId: string; huayName: string } {
  if (huayKey.startsWith('id:')) {
    return { huayId: huayKey.slice(3), huayName: '' }
  }
  if (huayKey.startsWith('name:')) {
    try {
      return { huayId: '', huayName: decodeURIComponent(huayKey.slice(5)) }
    } catch {
      return { huayId: '', huayName: huayKey.slice(5) }
    }
  }
  return { huayId: huayKey, huayName: '' }
}

export function matchesHuayKey(sheet: BerThongSheet, huayKey: string) {
  return huayKeyOf(sheet) === huayKey
}

export function groupSheetsByHuay(sheets: BerThongSheet[], nowMs = Date.now()): BerThongHuayGroup[] {
  const map = new Map<string, BerThongHuayGroup>()
  for (const sheet of sheets) {
    const key = huayKeyOf(sheet)
    let group = map.get(key)
    if (!group) {
      group = {
        key,
        huayId: String(sheet.huayId || ''),
        huayName: String(sheet.huayName || 'อื่นๆ').trim() || 'อื่นๆ',
        openSheets: [],
        closedSheets: [],
        soonestCloseAt: null,
      }
      map.set(key, group)
    }
    if (isSheetBuyable(sheet, nowMs)) {
      group.openSheets.push(sheet)
      if (sheet.closeAt) {
        if (!group.soonestCloseAt || new Date(sheet.closeAt) < new Date(group.soonestCloseAt)) {
          group.soonestCloseAt = sheet.closeAt
        }
      }
    } else {
      group.closedSheets.push(sheet)
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const aOpen = a.openSheets.length
    const bOpen = b.openSheets.length
    if (aOpen !== bOpen) return bOpen - aOpen
    return a.huayName.localeCompare(b.huayName, 'th')
  })
}
